import React, { useState, useRef, useEffect } from 'react';
import { Phone, Mail, UserPlus, ChevronDown, Menu, X } from 'lucide-react';
import { Button } from './ui/button';
import { Link, useLocation, useNavigate } from 'react-router-dom';

export const Header = () => {
  const [tmcDropdown, setTmcDropdown] = useState(false);
  const [resultsDropdown, setResultsDropdown] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef(null);
  const resultsDropdownRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Fonction pour scroll fluide vers une section
  const handleSmoothScroll = (e, sectionId) => {
    e.preventDefault();
    setMobileMenuOpen(false);
    setTmcDropdown(false);
    setResultsDropdown(false);
    
    const scrollToSection = () => {
      const element = document.getElementById(sectionId);
      if (element) {
        // Scroll directement au début de la section avec marge pour le header
        const headerOffset = 80; // Hauteur approximative du header fixe
        const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
        const offsetPosition = elementPosition - headerOffset;
        
        window.scrollTo({
          top: Math.max(0, offsetPosition),
          behavior: 'smooth'
        });
      }
    };
    
    // Si on est sur la page d'accueil, scroll direct
    if (location.pathname === '/') {
      // Délai pour laisser le menu se fermer
      setTimeout(scrollToSection, 100);
    } else {
      // Sinon, naviguer vers la page d'accueil puis scroll
      navigate('/');
      setTimeout(scrollToSection, 400);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setTmcDropdown(false);
      }
      if (resultsDropdownRef.current && !resultsDropdownRef.current.contains(event.target)) {
        setResultsDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleTmcDropdown = () => {
    setTmcDropdown(!tmcDropdown);
    setResultsDropdown(false);
  };

  const toggleResultsDropdown = () => {
    setResultsDropdown(!resultsDropdown);
    setTmcDropdown(false);
  };

  const closeDropdown = () => {
    setTmcDropdown(false);
    setResultsDropdown(false);
  };

  return (
    <header className="bg-white sticky top-0 z-50 shadow-md font-['Poppins']">
      {/* Top bar - Jeu concours Instagram */}
      <div className="hidden md:flex justify-center items-center py-2 bg-[#0A2320]">
        <a 
          href="https://www.instagram.com/tm_conciergerie?igsh=a2VnMnViYXBpa3Rn" 
          target="_blank" 
          rel="noopener noreferrer"
          className="flex items-center gap-2 text-white text-sm font-medium tracking-wide hover:text-[#D4AF37] transition-colors"
        >
          <span className="text-[#D4AF37]">🎉</span>
          Participez à notre jeu concours ! Suivez-nous sur Instagram pour en savoir plus !
          <span className="text-[#D4AF37]">🎉</span>
        </a>
      </div>

      <div className="container mx-auto px-4">
        {/* Main header */}
        <div className="flex justify-between items-center py-3 md:py-4">
          {/* Logo - AGRANDI significativement */}
          <Link to="/" className="flex items-center" onClick={closeDropdown}>
            <img 
              src="https://customer-assets.emergentagent.com/job_majordhome-care/artifacts/n4z4brh0_LOGO%20BLANC.png" 
              alt="The MajordHome Conciergerie" 
              className="h-20 md:h-28 lg:h-32 object-contain filter invert"
            />
          </Link>
          
          {/* Desktop Navigation - Couleurs distinctes du footer */}
          <nav className="hidden xl:flex items-center gap-5">
            {/* TMC Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button 
                onClick={toggleTmcDropdown}
                className={`flex items-center gap-1 transition-colors font-semibold text-base ${tmcDropdown ? 'text-[#0A2320]' : 'text-[#153254] hover:text-[#0A2320]'}`}
              >
                À propos
                <ChevronDown className={`h-4 w-4 transition-transform ${tmcDropdown ? 'rotate-180' : ''}`} />
              </button>
              {tmcDropdown && (
                <div className="absolute top-full left-0 mt-2 bg-white rounded-xl shadow-xl py-3 min-w-[220px] border border-gray-100">
                  <Link 
                    to="/tmc" 
                    onClick={() => { closeDropdown(); window.scrollTo(0, 0); }}
                    className="block px-5 py-2.5 text-[#153254] hover:bg-[#F8FAF9] hover:text-[#0A2320] transition-colors font-medium"
                  >
                    Notre histoire
                  </Link>
                  <Link 
                    to="/tmc#equipe" 
                    onClick={() => { closeDropdown(); window.scrollTo(0, 0); }}
                    className="block px-5 py-2.5 text-[#153254] hover:bg-[#F8FAF9] hover:text-[#0A2320] transition-colors font-medium"
                  >
                    Notre équipe
                  </Link>
                  <Link 
                    to="/tmc#engagements" 
                    onClick={() => { closeDropdown(); window.scrollTo(0, 0); }}
                    className="block px-5 py-2.5 text-[#153254] hover:bg-[#F8FAF9] hover:text-[#0A2320] transition-colors font-medium"
                  >
                    Nos engagements
                  </Link>
                </div>
              )}
            </div>

            <a 
              href="/#services" 
              onClick={(e) => { closeDropdown(); handleSmoothScroll(e, 'services'); }} 
              className="text-[#153254] hover:text-[#0A2320] transition-colors font-semibold text-base"
            >
              Nos services
            </a>
            <Link 
              to="/tarifs" 
              onClick={() => { closeDropdown(); window.scrollTo(0, 0); }}
              className="text-[#153254] hover:text-[#0A2320] transition-colors font-semibold text-base"
            >
              Tarifs
            </Link>
            <a 
              href="/#zone" 
              onClick={(e) => { closeDropdown(); handleSmoothScroll(e, 'zone'); }} 
              className="text-[#153254] hover:text-[#0A2320] transition-colors font-semibold text-base"
            >
              Zone d'intervention
            </a>
            
            {/* Nos Résultats Dropdown */}
            <div className="relative" ref={resultsDropdownRef}>
              <button 
                onClick={toggleResultsDropdown}
                className={`flex items-center gap-1 transition-colors font-semibold text-base ${resultsDropdown ? 'text-[#0A2320]' : 'text-[#153254] hover:text-[#0A2320]'}`}
              >
                Nos Résultats
                <ChevronDown className={`h-4 w-4 transition-transform ${resultsDropdown ? 'rotate-180' : ''}`} />
              </button>
              {resultsDropdown && (
                <div className="absolute top-full left-0 mt-2 bg-white rounded-xl shadow-xl py-3 min-w-[220px] border border-gray-100">
                  <a 
                    href="/#avant-apres" 
                    onClick={(e) => { closeDropdown(); handleSmoothScroll(e, 'avant-apres'); }}
                    className="block px-5 py-2.5 text-[#153254] hover:bg-[#F8FAF9] hover:text-[#0A2320] transition-colors font-medium"
                  >
                    Avant / Après
                  </a>
                  <a 
                    href="/#temoignages" 
                    onClick={(e) => { closeDropdown(); handleSmoothScroll(e, 'temoignages'); }}
                    className="block px-5 py-2.5 text-[#153254] hover:bg-[#F8FAF9] hover:text-[#0A2320] transition-colors font-medium"
                  >
                    Témoignages
                  </a>
                </div>
              )}
            </div>
            
            <a 
              href="/#contact" 
              onClick={(e) => { closeDropdown(); handleSmoothScroll(e, 'contact'); }} 
              className="text-[#153254] hover:text-[#0A2320] transition-colors font-semibold text-base"
            >
              Contact & FAQ
            </a>
          </nav>

          {/* Desktop CTAs - TOUS EN VERT #0A2320 */}
          <div className="hidden xl:flex items-center gap-3 ml-6">
            <Link to="/candidature" onClick={() => window.scrollTo(0, 0)}>
              <Button 
                variant="outline" 
                size="sm" 
                className="border-2 border-[#0A2320] text-[#0A2320] hover:bg-[#0A2320] hover:text-white font-semibold rounded-xl"
              >
                <UserPlus className="mr-1.5 h-4 w-4" />
                Nous recrutons
              </Button>
            </Link>
            <Link to="/devis-premium" onClick={() => window.scrollTo(0, 0)}>
              <Button 
                size="sm" 
                className="bg-[#0A2320] hover:bg-[#0d2e2a] text-white font-semibold rounded-xl shadow-lg"
              >
                Demander un devis
              </Button>
            </Link>
          </div>

          {/* Mobile menu button - visible on tablets and mobile */}
          <button 
            className="xl:hidden text-[#153254] p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="xl:hidden py-4 border-t border-gray-100">
            <nav className="flex flex-col space-y-3">
              <Link 
                to="/tmc" 
                onClick={() => { setMobileMenuOpen(false); window.scrollTo(0, 0); }}
                className="text-[#153254] hover:text-[#0A2320] font-semibold py-2"
                data-testid="mobile-apropos"
              >
                À propos
              </Link>
              <button 
                onClick={(e) => handleSmoothScroll(e, 'services')} 
                className="text-[#153254] hover:text-[#0A2320] font-semibold py-2 text-left"
                data-testid="mobile-services"
              >
                Nos services
              </button>
              <Link 
                to="/tarifs" 
                onClick={() => { setMobileMenuOpen(false); window.scrollTo(0, 0); }}
                className="text-[#153254] hover:text-[#0A2320] font-semibold py-2"
                data-testid="mobile-tarifs"
              >
                Tarifs
              </Link>
              <button 
                onClick={(e) => handleSmoothScroll(e, 'zone')} 
                className="text-[#153254] hover:text-[#0A2320] font-semibold py-2 text-left"
                data-testid="mobile-zone"
              >
                Zone d'intervention
              </button>
              <button 
                onClick={(e) => handleSmoothScroll(e, 'avant-apres')} 
                className="text-[#153254] hover:text-[#0A2320] font-semibold py-2 text-left"
                data-testid="mobile-avant-apres"
              >
                Avant / Après
              </button>
              <button 
                onClick={(e) => handleSmoothScroll(e, 'temoignages')} 
                className="text-[#153254] hover:text-[#0A2320] font-semibold py-2 text-left"
                data-testid="mobile-temoignages"
              >
                Témoignages
              </button>
              <button 
                onClick={(e) => handleSmoothScroll(e, 'contact')} 
                className="text-[#153254] hover:text-[#0A2320] font-semibold py-2 text-left"
                data-testid="mobile-contact"
              >
                Contact & FAQ
              </button>
              <div className="flex flex-col gap-3 pt-4 border-t border-gray-100">
                <Link to="/devis-premium" onClick={() => { setMobileMenuOpen(false); window.scrollTo(0, 0); }}>
                  <Button 
                    size="sm" 
                    className="bg-[#0A2320] hover:bg-[#0d2e2a] text-white font-semibold w-full rounded-xl"
                  >
                    Demander un devis
                  </Button>
                </Link>
                <Link to="/candidature" onClick={() => { setMobileMenuOpen(false); window.scrollTo(0, 0); }}>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="border-2 border-[#0A2320] text-[#0A2320] w-full rounded-xl"
                  >
                    <UserPlus className="mr-1.5 h-4 w-4" />
                    Nous recrutons
                  </Button>
                </Link>
              </div>
              <div className="flex flex-col gap-2 pt-4 text-sm">
                <a href="tel:0981543842" className="flex items-center gap-2 text-[#153254] font-medium">
                  <Phone className="h-4 w-4" />
                  09 81 54 38 42
                </a>
                <a href="mailto:tmc60110@gmail.com" className="flex items-center gap-2 text-[#153254] font-medium">
                  <Mail className="h-4 w-4" />
                  tmc60110@gmail.com
                </a>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};
